import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation } from '@angular/core';
import { NgSwitch, NgSwitchCase } from '@angular/common';
import { RAG_ASSISTANT_ICON_DATA_URL } from './rag-icon.data';

export type RagIconName =
  | 'assistant'
  | 'sparkle' | 'chat' | 'close' | 'send' | 'stop' | 'refresh' | 'minimize'
  | 'collection' | 'check' | 'alert' | 'help' | 'warning' | 'external'
  | 'copy' | 'copied' | 'chevron-down' | 'chevron-up' | 'eye';

@Component({
  selector: 'rag-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [NgSwitch, NgSwitchCase],
  template: `
    <svg
      class="rag-icon"
      [attr.width]="size"
      [attr.height]="size"
      [attr.viewBox]="name === 'assistant' ? '0 0 200 200' : '0 0 24 24'"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
      focusable="false"
      [ngSwitch]="name"
    >
      <!-- Keep the complete supplied icon, including the magnifying glass. -->
      <svg:image *ngSwitchCase="'assistant'" [attr.href]="assistantIcon" x="0" y="0" width="200" height="200" preserveAspectRatio="xMidYMid meet" />
      <svg:path *ngSwitchCase="'sparkle'" d="M12 3l1.85 4.65L18.5 9.5l-4.65 1.85L12 16l-1.85-4.65L5.5 9.5l4.65-1.85L12 3z" />
      <svg:path *ngSwitchCase="'sparkle'" d="M18.5 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" />
      <svg:path *ngSwitchCase="'chat'" d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-3.8-.9L4 21l1.9-4.7A8.4 8.4 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3 8.5 8.5 0 0 1 21 11.5z" />
      <svg:path *ngSwitchCase="'close'" d="M18 6L6 18M6 6l12 12" />
      <svg:path *ngSwitchCase="'send'" d="M12 19V5M5 12l7-7 7 7" />
      <svg:rect *ngSwitchCase="'stop'" x="6.5" y="6.5" width="11" height="11" rx="2" />
      <ng-container *ngSwitchCase="'refresh'">
        <svg:path d="M21 4v6h-6" />
        <svg:path d="M20.2 13.5A8.5 8.5 0 1 1 18.1 5.4L21 8.2" />
      </ng-container>
      <svg:path *ngSwitchCase="'minimize'" d="M5 12h14" />
      <ng-container *ngSwitchCase="'collection'">
        <svg:path d="M12 3L2.5 7.5 12 12l9.5-4.5L12 3z" />
        <svg:path d="M2.5 12.2 12 16.7l9.5-4.5" />
        <svg:path d="M2.5 16.7 12 21.2l9.5-4.5" />
      </ng-container>
      <ng-container *ngSwitchCase="'check'">
        <svg:path d="M21.5 11.1V12a9.5 9.5 0 1 1-5.6-8.7" />
        <svg:path d="M21.5 4.5 12 14.1l-2.8-2.8" />
      </ng-container>
      <ng-container *ngSwitchCase="'alert'">
        <svg:circle cx="12" cy="12" r="9.2" />
        <svg:path d="M12 7.6v4.9" />
        <svg:path d="M12 16.2h.01" />
      </ng-container>
      <ng-container *ngSwitchCase="'help'">
        <svg:circle cx="12" cy="12" r="9.2" />
        <svg:path d="M9.3 9.2a2.8 2.8 0 0 1 5.4.9c0 1.9-2.7 2.5-2.7 3.6" />
        <svg:path d="M12 17h.01" />
      </ng-container>
      <ng-container *ngSwitchCase="'warning'">
        <svg:path d="M10.3 3.9 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        <svg:path d="M12 9.2v4.1" />
        <svg:path d="M12 16.9h.01" />
      </ng-container>
      <ng-container *ngSwitchCase="'external'">
        <svg:path d="M18 13.2V19a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2h5.8" />
        <svg:path d="M15 3h6v6" />
        <svg:path d="M10.2 13.8 21 3" />
      </ng-container>
      <ng-container *ngSwitchCase="'copy'">
        <svg:rect x="9" y="9" width="12.2" height="12.2" rx="2.2" />
        <svg:path d="M5.5 15H4.8A2 2 0 0 1 2.8 13V4.8a2 2 0 0 1 2-2H13a2 2 0 0 1 2 2v.7" />
      </ng-container>
      <svg:path *ngSwitchCase="'copied'" d="M20 6.5 9.5 17 4 11.5" />
      <svg:path *ngSwitchCase="'chevron-down'" d="M6 9.5l6 6 6-6" />
      <svg:path *ngSwitchCase="'chevron-up'" d="M18 14.5l-6-6-6 6" />
      <ng-container *ngSwitchCase="'eye'">
        <svg:path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
        <svg:circle cx="12" cy="12" r="3" />
      </ng-container>
    </svg>
  `,
})
export class RagIconComponent {
  protected readonly assistantIcon = RAG_ASSISTANT_ICON_DATA_URL;
  @Input({ required: true }) name!: RagIconName;
  @Input() size = 18;
}
